import { useState } from "react";
import { ArrowUpRight, Clock3, X } from "lucide-react";

import type { ActionCard as ActionCardData } from "@/types/actions";
import { Button } from "@/components/ui/button";
import { OverlayShell } from "@/components/ui/overlay";
import { ActionCardContent } from "@/components/today/action-card";
import { ActionCardActions } from "@/components/today/action-card-actions";
import { ActionQueueLabels } from "@/lib/action-queue";
import { djs } from "@/lib/djs";

export function ConversationContextAction({ action }: { action: ActionCardData }) {
	const [detailsOpen, setDetailsOpen] = useState(false);
	const hasDraft =
		!!action.draft &&
		action.surface === "conversation" &&
		action.status === "aberta" &&
		action.can_manage;
	const reviewAt = action.next_review_at ?? action.due_at;
	const reviewLabel = action.next_review_at ? "Retorno" : "Prazo";
	const detailLabel = hasDraft ? "Ver rascunho" : "Ver detalhes";
	const waitingFor = action.waiting_for
		? ActionQueueLabels.waitingFor[action.waiting_for]
		: "retorno";

	return (
		<article
			className="py-5 last:pb-0"
			data-component="conversation-context-action"
			data-action-id={action.id}
		>
			<div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
				<span className="capitalize">{ActionQueueLabels.role[action.role]}</span>
				{action.priority === "alta" && <span className="font-medium text-warning">Prioridade</span>}
			</div>
			<h4 className="mt-2 break-words text-sm font-semibold leading-6 text-foreground">
				{action.task}
			</h4>
			{action.status === "aguardando" && (
				<p className="mt-2 flex items-start gap-1.5 text-xs leading-5 text-muted-foreground">
					<Clock3 className="mt-0.5 size-3.5 shrink-0" />
					Aguardando {waitingFor}
				</p>
			)}
			{reviewAt && (
				<p className="mt-1 text-xs text-muted-foreground">
					{reviewLabel} em {djs(reviewAt).tz("America/Sao_Paulo").format("DD/MM HH:mm")}
				</p>
			)}
			<div className="mt-3 flex flex-wrap items-center justify-between gap-x-2 gap-y-3">
				<button
					type="button"
					onClick={() => setDetailsOpen(true)}
					className="inline-flex min-h-8 items-center gap-1 rounded-sm text-xs font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
				>
					{detailLabel} <ArrowUpRight className="size-3.5" />
				</button>
				<ActionCardActions action={action} />
			</div>
			{detailsOpen && (
				<OverlayShell
					label="Detalhes da pendência"
					onClose={() => setDetailsOpen(false)}
					className="items-center justify-center p-4"
				>
					<section className="relative flex max-h-[85dvh] w-full max-w-xl flex-col overflow-hidden rounded-xl bg-card shadow-overlay">
						<header className="flex items-center justify-between gap-3 border-b border-border px-5 py-3">
							<h2 className="font-semibold text-foreground">Pendência</h2>
							<Button
								variant="ghost"
								size="icon"
								aria-label="Fechar pendência"
								onClick={() => setDetailsOpen(false)}
							>
								<X />
							</Button>
						</header>
						<div className="@container overflow-y-auto overscroll-contain p-5">
							<ActionCardContent action={action} showRole />
						</div>
					</section>
				</OverlayShell>
			)}
		</article>
	);
}
